import { DynamicStructure, EMPTY_VALUE, FieldName, FieldValues, isEmpty } from "../../../Schema";
import { ErrorMessage } from "../../../validation/ValidationManager";

/**
 * The two ways a row can contradict itself about checking in.
 *
 * Both used to be hand-written in the CSV import, alongside a copy of the period option lists
 * and a re-statement of the requirements the fields already declare. Only these two were
 * doing any work the schema was not: they are relations between fields rather than facts
 * about one, so no single field's `data` aspect could express them.
 *
 * They are reported rather than silently corrected. `assignedValue` already normalises both
 * situations away — clearing `checkInRequired` for an unbookable equipment, clearing the
 * periods when check-in is off — which is right for a form, where the user cannot get into
 * either state and the disabled inputs simply settle. A file can say anything, and quietly
 * dropping what it said is how an import reports success having ignored half the row.
 */

/** A check-in requirement on an equipment that cannot be booked at all. */
export const CHECK_IN_WITHOUT_BOOKING =
  "Check in cannot be activated with isBookable set to FALSE";

/** A check-in period given without the requirement that would make it mean anything. */
export const PERIOD_WITHOUT_CHECK_IN = "Missing field: Check in required";

/**
 * Rule for `checkInRequired`: check-in cannot be switched on for an unbookable equipment.
 */
export const checkInWithoutBooking: DynamicStructure<ErrorMessage, FieldName[]> = {
  getter: ({ isBookable, checkInRequired }: FieldValues): ErrorMessage =>
    isBookable === false && checkInRequired === true ?
      CHECK_IN_WITHOUT_BOOKING :
      EMPTY_VALUE,
  fields: ["isBookable", "checkInRequired"],
};

/**
 * Rule for the two period fields: a period means nothing unless check-in is required.
 *
 * The field's own value is read, not just the two it depends on, because a rule is also
 * reported when the row supplied one of its inputs — so `checkInRequired: No` alone would
 * otherwise raise this against a period the file never mentioned.
 *
 * @param periodField - The period field the rule is declared on.
 * @returns The rule to put on that field's `validation.custom`.
 */
export const periodWithoutCheckIn = (
  periodField: FieldName
): DynamicStructure<ErrorMessage, FieldName[]> => ({
  getter: (values: FieldValues): ErrorMessage =>
    !isEmpty(values[periodField]) && values.checkInRequired !== true ?
      PERIOD_WITHOUT_CHECK_IN :
      EMPTY_VALUE,
  fields: ["checkInRequired", periodField],
});
